import { useMemo, useState } from "react";
import { Gift, ArrowRight } from "lucide-react";
import { DEPOSIT_BONUS_TIERS, getDepositBonus } from "../../data/fees";
import { useLanguage } from "../../contexts/LanguageContext";

const COPY = {
    en: { title: "Top-up bonus", sub: "The more you fund in a single top-up, the more instant credit lands in your balance.", planned: "Planned top-up", youGet: "Instant credit", total: "Total balance", from: "From", maxed: "You're at the top bonus tier — maximum instant credit.", toNext: (amt, pct) => `Top up ${amt} more to unlock +${pct}%` },
    tr: { title: "Yükleme bonusu", sub: "Tek seferde ne kadar çok yüklersen, bakiyene o kadar çok anında kredi geçer.", planned: "Planlanan yükleme", youGet: "Anında kredi", total: "Toplam bakiye", from: "Şundan başlar", maxed: "En üst bonus kademesindesin — maksimum anında kredi.", toNext: (amt, pct) => `+%${pct} için ${amt} daha yükle` },
    es: { title: "Bono de recarga", sub: "Cuanto más recargas en una sola operación, más crédito instantáneo recibes.", planned: "Recarga planeada", youGet: "Crédito instantáneo", total: "Saldo total", from: "Desde", maxed: "Estás en el nivel máximo de bono.", toNext: (amt, pct) => `Recarga ${amt} más para +${pct}%` },
    pt: { title: "Bônus de recarga", sub: "Quanto mais você abastece de uma vez, mais crédito instantâneo entra no saldo.", planned: "Recarga planejada", youGet: "Crédito instantâneo", total: "Saldo total", from: "A partir de", maxed: "Você está no nível máximo de bônus.", toNext: (amt, pct) => `Recarregue ${amt} a mais para +${pct}%` },
    ru: { title: "Бонус за пополнение", sub: "Чем больше сумма одного пополнения, тем больше мгновенный кредит на баланс.", planned: "Планируемое пополнение", youGet: "Мгновенный кредит", total: "Итоговый баланс", from: "От", maxed: "Вы на максимальном уровне бонуса.", toNext: (amt, pct) => `Пополните ещё ${amt} для +${pct}%` },
};

const PRESETS = [2500, 10000, 25000, 50000];
const fmt = (n) => "$" + Math.round(n).toLocaleString("en-US");

export default function WalletBonusCard() {
    const { language } = useLanguage();
    const c = COPY[language] || COPY.en;
    const [amount, setAmount] = useState(10000);
    const { bonusPct, bonus } = useMemo(() => getDepositBonus(amount), [amount]);
    const nextTier = useMemo(() => DEPOSIT_BONUS_TIERS.find((tI) => tI.min > amount), [amount]);

    return (
        <div data-testid="wallet-bonus-card" className="bg-card border border-[#2FA57C]/25 rounded-2xl p-5 md:p-6">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground mb-4">
                <Gift className="w-4 h-4 text-[#2FA57C]" /> {c.title}
            </div>
            <p className="text-sm text-muted-foreground mb-5 max-w-xl">{c.sub}</p>

            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
                <div>
                    <div className="flex flex-wrap gap-2">
                        {PRESETS.map((p) => (
                            <button key={p} onClick={() => setAmount(p)} data-testid={`wallet-bonus-preset-${p}`}
                                className={`px-3 py-1.5 rounded-md text-sm font-mono border transition-colors ${amount === p ? "border-[#2FA57C]/60 bg-[#2FA57C]/10 text-[#2FA57C]" : "border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20"}`}>
                                {fmt(p)}
                            </button>
                        ))}
                    </div>
                    <input type="range" min={500} max={75000} step={500} value={amount} onChange={(e) => setAmount(Number(e.target.value))} data-testid="wallet-bonus-slider" className="w-full mt-4 accent-[#2FA57C]" />

                    <div className="mt-4 rounded-lg border border-[#2FA57C]/25 bg-[#2FA57C]/[0.06] px-4 py-3 text-sm flex items-center gap-2" data-testid="wallet-bonus-next">
                        <ArrowRight className="w-4 h-4 text-[#2FA57C] shrink-0" />
                        <span className="text-foreground/90">
                            {nextTier ? c.toNext(fmt(nextTier.min - amount), nextTier.bonusPct) : c.maxed}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center self-start">
                    <div className="rounded-lg border border-white/[0.08] p-3">
                        <p className="text-[9px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{c.planned}</p>
                        <p className="font-display text-lg md:text-xl mt-1 tabular-nums">{fmt(amount)}</p>
                    </div>
                    <div className="rounded-lg border border-[#2FA57C]/30 bg-[#2FA57C]/[0.06] p-3">
                        <p className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#2FA57C]">+{bonusPct}%</p>
                        <p className="font-display text-lg md:text-xl mt-1 text-[#2FA57C] tabular-nums" data-testid="wallet-bonus-amount">{fmt(bonus)}</p>
                    </div>
                    <div className="rounded-lg border border-white/[0.08] p-3">
                        <p className="text-[9px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{c.total}</p>
                        <p className="font-display text-lg md:text-xl mt-1 tabular-nums">{fmt(amount + bonus)}</p>
                    </div>
                </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-1.5 pt-4 border-t border-white/[0.06]">
                {DEPOSIT_BONUS_TIERS.map((tI) => (
                    <span key={tI.min} className="text-xs font-mono text-muted-foreground">
                        {c.from} <span className="text-foreground">{fmt(tI.min)}</span> <span className="text-[#2FA57C]">+{tI.bonusPct}%</span>
                    </span>
                ))}
            </div>
        </div>
    );
}
